import Executive from '../../framework/rmi-util'
import { register } from '../../framework/rmi-server-util'
import { CheckApiKey, CheckPolicy, Resource } from '../../framework/permission-util'
import { RT } from '../permissions'
import { tourOperatorTechnical } from './TourOperatorTechnical'
import type { 
	CreateCustomerRequest,
	CreateB2BPartnerRequest,
	CreateB2BPartnerUserRequest,
	CreateContractRequest,
	TourOperatorCustomer,
	B2BPartner,
	B2BPartnerUser,
	Contract,
	ContractDetails,
	B2BPartnerUsersResponse,
	CreateB2BPartnerUserByEmailRequest,
	CreateB2BPartnerUserByEmailResponse,
	UpdateContractStatusByEmailRequest,
	UpdateContractStatusByEmailResponse,
	CreateTourOperatorWorkflowRequest,
	TourOperatorWorkflowResponse,
} from './tour-operator-types'
import { TourOperatorUtil } from './TourOperatorUtil'
import type { GeneralProvision, SpecificProvision } from './provision.entities'

// TODO: add CheckPolicy / CheckApiKey decorators for all endpoints

/**
 * Tour Operator Business Operations Executive
 * 
 * This executive provides high-level business operations for managing 
 * tour operators in our PEAK platform. All methods represent business
 * processes that executives and stakeholders can easily understand.
 */
export default class TourOperatorExecutive extends Executive {

	initServer() {
	}

	// =================================================================
	// COMPLETE TOUR OPERATOR ONBOARDING
	// =================================================================

	/**
	 * Complete Tour Operator Onboarding Process
	 * 
	 * This single operation handles the entire process of onboarding 
	 * a new tour operator, including:
	 * - Customer account setup
	 * - Partner profile creation
	 * - Contract establishment
	 * - User access configuration (questionable as of now)
	 * 
	 * @param metisId - The destination system identifier
	 * @param request - Complete tour operator details
	 * @returns Full onboarding results with status
	 */
	@CheckApiKey()
	async onboardNewTourOperator(metisId: string, request: CreateTourOperatorWorkflowRequest): Promise<TourOperatorWorkflowResponse> {
		return tourOperatorTechnical.createTourOperatorWorkflow(metisId, request)
	}

	/**
	 * Onboard Tour Operator from Signed Contract
	 * 
	 * This method is triggered when a Tour Operator signs their contract.
	 * It creates the PEAK B2B Partner and Customer accounts based on the
	 * signed contract and contractor information.
	 * 
	 * @param contractorId - The contractor ID from the signed contract
	 * @returns Onboarding result with PEAK customer and partner IDs
	 */
	@CheckPolicy()
	async onboardTourOperatorFromContract(@Resource(RT.CONTRACTOR) contractorId: string, language: string): Promise<{
		success: boolean
		b2bPartnerUserId?: string
		peakCustomerId?: string
		errors?: string[]
	}> {
		return tourOperatorTechnical.onboardTourOperatorFromContract(contractorId, language)
	}

	// =================================================================
	// INDIVIDUAL BUSINESS ENTITY OPERATIONS
	// =================================================================

	/**
	 * Establish Customer Account for Tour Operator
	 * 
	 * Creates the fundamental customer record in our system,
	 * which serves as the foundation for all tour operator operations.
	 */
	@CheckApiKey()
	async establishCustomerAccount(metisId: string, request: CreateCustomerRequest): Promise<TourOperatorCustomer> {
		return tourOperatorTechnical.createCustomer(metisId, request)
	}

	/**
	 * Create Partner Business Profile
	 * 
	 * Sets up the business profile for the tour operator partner,
	 * including their business type, locale, and branding.
	 */
	@CheckApiKey()
	async createPartnerBusinessProfile(metisId: string, request: CreateB2BPartnerRequest): Promise<B2BPartner> {
		return tourOperatorTechnical.createB2BPartner(metisId, request)
	}

	/**
	 * Register Partner User Access
	 * 
	 * Creates user access for individuals who will manage 
	 * the tour operator account on behalf of the partner.
	 */
	@CheckApiKey()
	async registerPartnerUserAccess(metisId: string, request: CreateB2BPartnerUserRequest): Promise<B2BPartnerUser> {
		return tourOperatorTechnical.createB2BPartnerUser(metisId, request)
	}

	/**
	 * Establish Business Contract
	 * 
	 * Creates the business contract defining terms, margins,
	 * payment methods, and operational parameters for the partnership.
	 */
	@CheckApiKey()
	async establishBusinessContract(metisId: string, request: CreateContractRequest): Promise<Contract> {
		return tourOperatorTechnical.createContract(metisId, request)
	}

	// =================================================================
	// PARTNER USER MANAGEMENT
	// =================================================================

	/**
	 * Find All Partner Users by Company Email
	 * 
	 * Retrieves all users associated with a tour operator partner,
	 * useful for account management and user administration.
	 */
	async findAllPartnerUsers(metisId: string, partnerEmail: string): Promise<B2BPartnerUsersResponse> {
		return tourOperatorTechnical.getB2BPartnerUsersByEmail(metisId, partnerEmail)
	}

	/**
	 * Add New User to Existing Partner
	 * 
	 * Adds a new team member to an existing tour operator account,
	 * with appropriate access levels and permissions.
	 */
	async addNewUserToPartner(metisId: string, request: CreateB2BPartnerUserByEmailRequest): Promise<CreateB2BPartnerUserByEmailResponse> {
		return tourOperatorTechnical.createB2BPartnerUserByEmail(metisId, request)
	}

	// =================================================================
	// CONTRACT ADMINISTRATION
	// =================================================================

	/**
	 * Update Contract Status by Partner Email
	 * 
	 * Activates or deactivates contracts for a tour operator partner.
	 * This directly affects their ability to operate and make bookings.
	 * 
	 * Supports precise filtering by:
	 * - Specific contract ID
	 * - Date range (start/end dates)
	 * 
	 * Examples:
	 * - Activate only contracts for the current year
	 * - Deactivate expired contracts
	 * - Update specific seasonal contracts
	 */
	async updatePartnerContractStatus(metisId: string, request: UpdateContractStatusByEmailRequest): Promise<UpdateContractStatusByEmailResponse> {
		return tourOperatorTechnical.updateContractStatusByEmail(metisId, request)
	}

	/**
	 * View Complete Contract Details
	 * 
	 * Retrieves comprehensive contract information including
	 * current status, effective dates, and operational parameters.
	 */
	async viewContractDetails(metisId: string, contractId: number): Promise<ContractDetails> {
		return tourOperatorTechnical.getContractDetails(metisId, contractId)
	}

	/**
	 * Adjust Contract Commission Rates
	 * 
	 * Updates the commission rate for specific product categories
	 * within a contract, allowing for dynamic pricing adjustments.
	 */
	async adjustContractCommissionRates(metisId: string, contractId: number, categoryId: number, commissionRate: number): Promise<void> {
		return tourOperatorTechnical.updateContractCategoryCommission(metisId, contractId, categoryId, commissionRate)
	}

	/**
	 * Review Contract Product Categories
	 * 
	 * Lists all product categories covered by a contract
	 * with their respective commission rates and terms.
	 */
	async reviewContractProductCategories(metisId: string, contractId: number): Promise<Array<{categoryId: number, commissionRate: number, individualSale: boolean, presalePeriodDays: number | null}>> {
		return tourOperatorTechnical.getContractCategories(metisId, contractId)
	}

	// =================================================================
	// ACCOUNT VERIFICATION AND STATUS CHECKS
	// =================================================================

	/**
	 * Verify Customer Account Exists
	 * 
	 * Checks if a customer account already exists for the given email
	 * before attempting to create a new tour operator.
	 */
	async verifyCustomerAccountExists(metisId: string, email: string): Promise<boolean> {
		return tourOperatorTechnical.customerExistsByEmail(metisId, email)
	}

	/**
	 * Check Partner Profile Status
	 * 
	 * Verifies if a partner profile has been established
	 * for the specified customer account.
	 */
	async checkPartnerProfileStatus(metisId: string, customerId: number): Promise<boolean> {
		return TourOperatorUtil.b2bPartnerExistsByCustomerId(metisId, customerId)
	}

	/**
	 * Verify Contract Registration
	 * 
	 * Confirms if a contract exists and is properly registered
	 * in the system for the given contract ID.
	 */
	async verifyContractRegistration(metisId: string, contractId: number): Promise<boolean> {
		return TourOperatorUtil.contractExistsById(metisId, contractId)
	}

	// =================================================================
	// DIRECT CONTRACT OPERATIONS
	// =================================================================

	/**
	 * Activate or Deactivate Contract
	 * 
	 * Directly changes the operational status of a contract,
	 * immediately affecting the partner's ability to conduct business.
	 */
	async updateContractOperationalStatus(metisId: string, contractId: number, isActive: boolean): Promise<void> {
		return TourOperatorUtil.updateContractStatus(metisId, contractId, isActive)
	}

	// TODO: Move to separate executive?
	// =================================================================
	// PROVISIONS
	// =================================================================
	/**
	 * Get or create general provision for service provider
	 */
	@CheckPolicy()
	async getOrUpsertGeneralProvision(@Resource(RT.PROVIDER) serviceProviderId: string, generalProvision: GeneralProvision): Promise<GeneralProvision> {
		let entry = await tourOperatorTechnical.getGeneralProvision(generalProvision.clientId, generalProvision.serviceProviderId)
		if (!entry) {
			entry = await tourOperatorTechnical.upsertGeneralProvision(generalProvision)
		}
		return entry
	}

	/**
	 * update general provision for service provider
	 */
	@CheckPolicy()
	async upsertGeneralProvision(@Resource(RT.PROVIDER) serviceProviderId: string, generalProvision: GeneralProvision): Promise<GeneralProvision> {
		return await tourOperatorTechnical.upsertGeneralProvision(generalProvision)
	}

	/**
	 * upsert specific provision for service provider
	 */
	@CheckPolicy()
	async upsertSpecificProvision(@Resource(RT.PROVIDER) serviceProviderId: string, generalProvisionId: string, specificProvision: SpecificProvision): Promise<SpecificProvision> {
		return await tourOperatorTechnical.upsertSpecificProvision(generalProvisionId, specificProvision)
	}

	/**
	 * delete specific provision for service provider
	 */
	@CheckPolicy()
	async deleteSpecificProvision(@Resource(RT.PROVIDER) serviceProviderId: string, id: string): Promise<void> {
		return await tourOperatorTechnical.deleteSpecificProvision(id)
	}

	/**
	 * check if user has unlocked tradehub applications
	 * @param serviceProviderId - optional; if not provided, will be resolved from user lookup by email
	 */
	async hasUnlockedTradehubApps(email: string, serviceProviderId?: string): Promise<boolean> {
		return await tourOperatorTechnical.hasUnlockedTradehubApps(email, serviceProviderId)
	}

	/**
	 * get tour operators
	 */
	@CheckPolicy()
	async getTourOperators(@Resource(RT.PROVIDER) serviceProviderId: string, clientId: string): Promise<any[]> {
		return await tourOperatorTechnical.getTourOperators(clientId)
	}

	/**
	 * Revoke a user's PEAK access.
	 * This is used when a user leaves the organization. Does not touch their PEAK
	 * customer record (order history, etc.) - see revokeB2BPartnerUserAccessByEmail.
	 */
	async deleteUserCompletely(metisId: string, email: string): Promise<{
		success: boolean
		revokedCustomerId?: number
		revokedPartnerUserId?: number
		error?: string
	}> {
		return await tourOperatorTechnical.revokeB2BPartnerUserAccessByEmail(metisId, email)
	}

	/**
	 * Delete Complete Tour Operator Setup
	 * 
	 * This method performs a comprehensive deletion of all Tour Operator entities:
	 * - All PEAK entities (B2B Partner, Customer, Contracts, Partner Users)
	 * - All MyServices invited users and invitations
	 * 
	 * This is used when a Tour Operator service provider is deleted.
	 */
	async deleteCompleteTourOperatorSetup(serviceProviderId: string, email: string, clientId: string): Promise<{
		success: boolean
		peakDeletion?: {
			deletedPartnerUsersCount: number
			deletedContractsCount: number
			deletedMainPartner: boolean
			deletedMainCustomer: boolean
		}
		myServicesDeletion?: {
			deletedInvitedUsersCount: number
			deletedInvitationsCount: number
		}
		errors?: string[]
	}> {
		return await tourOperatorTechnical.deleteCompleteTourOperatorSetupWithContext(serviceProviderId, email, clientId)
	}
}

register(TourOperatorExecutive) 